import { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatPumpProgramming } from "./utils/formatPumpProgramming";

export const TitulacionLenta = ({ peso }) => {
	const [rango, setRango] = useState("1-10");

	const pesoNum = parseFloat(peso) || 0;
	const concentracionMgPorMl = 5; // 5 mg/mL
	const diasPorMes = 30; // Asumiendo meses de 30 días

	// Array de dosis por semana (26 semanas)
	const dosisPorSemana = Array.from({ length: 26 }, (_, i) => {
		if (i < 4) {
			return 1.25 * (i + 1);
		} else {
			return 5 + (2.5 * (i - 3));
		}
	});

	const redondear = (valor, decimales = 3) => parseFloat(valor.toFixed(decimales));
	const redondearHaciaArribaPorPaso = (valor, paso) => {
		const multiplicador = Math.round(1 / paso);
		return Math.ceil(valor * multiplicador - 1e-9) / multiplicador;
	};

	// Calcular tasa de infusión para una dosis dada
	const calcularTasaInfusion = (dosis) => {
		return (dosis * pesoNum * 60) / (concentracionMgPorMl * 1000000);
	};

	// Calcular volumen diario (tasa de infusión x 24 horas)
	const calcularVolumenDiario = (tasaInfusion) => {
		return tasaInfusion * 24;
	};

	// Calcular volumen de infusión y programación de bomba
	const calcularVolumenYProgramacion = (tasaInfusion) => {
		const volumenInfusionUiH = tasaInfusion * 100;
		const pasoProgramacion = volumenInfusionUiH < 1 ? 0.025 : 0.05;
		const programacionBomba = redondearHaciaArribaPorPaso(volumenInfusionUiH, pasoProgramacion);
		
		return {
			volumenInfusion: redondear(volumenInfusionUiH, 3),
			programacionBomba: redondear(programacionBomba, 3)
		};
	};

	// Calcular carga de volumen en jeringa (suma de 3 días consecutivos)
	const calcularCargaJeringa = (datos) => {
		const datosConCarga = [...datos];
		
		for (let i = 0; i < datosConCarga.length; i += 3) {
			// Sumar los daily volumes de los próximos 3 días (o los que queden)
			let suma = 0;
			const diasASumar = Math.min(3, datosConCarga.length - i);
			
			for (let j = 0; j < diasASumar; j++) {
				suma += datosConCarga[i + j].volumenDiario;
			}
			
			// Asignar la suma al primer día del grupo de 3
			datosConCarga[i].cargaJeringa = redondear(suma, 4);
			
			// Calcular carga práctica (cargaJeringa + 0.2 redondeado a 1 decimal)
			if (datosConCarga[i].cargaJeringa !== null) {
				datosConCarga[i].cargaPractica = redondear(datosConCarga[i].cargaJeringa + 0.2, 1);
			}
			
			// Los otros 2 días del grupo quedan con carga en null
			for (let j = 1; j < diasASumar; j++) {
				datosConCarga[i + j].cargaJeringa = null;
				datosConCarga[i + j].cargaPractica = null;
			}
		}
		
		return datosConCarga;
	};

	// Calcular total mensual de volumen práctico
	const calcularTotalMensual = (datos) => {
		const datosConTotal = [...datos];
		const meses = {};
		
		// Agrupar datos por mes
		datosConTotal.forEach(dato => {
			if (!meses[dato.mes]) {
				meses[dato.mes] = [];
			}
			meses[dato.mes].push(dato);
		});
		
		// Calcular total por mes y asignar al día 30
		Object.keys(meses).forEach(mes => {
			const datosMes = meses[mes];
			const totalMes = datosMes.reduce((sum, dato) => {
				return sum + (dato.cargaPractica || 0);
			}, 0);
			
			// Encontrar el día 30 del mes (si existe)
			const dia30 = datosMes.find(dato => dato.dia === 30);
			if (dia30) {
				dia30.totalMensual = redondear(totalMes, 1);
				dia30.esDia30 = true; // Marcar como día 30 para el estilo
			}
		});
		
		return datosConTotal;
	};

	// Generar datos día por día para el PDF
	const generarDatosPorDia = () => {
		const datos = [];
		let diaGlobal = 1;
		let mesActual = 1;

		for (let semana = 0; semana < dosisPorSemana.length; semana++) {
			const dosis = dosisPorSemana[semana];
			const tasaInfusion = calcularTasaInfusion(dosis);
			const volumenDiario = calcularVolumenDiario(tasaInfusion);
			const { volumenInfusion, programacionBomba } = calcularVolumenYProgramacion(tasaInfusion);
			
			// 7 días por semana
			for (let diaSemana = 1; diaSemana <= 7; diaSemana++) {
				// Calcular día del mes (1-30) y mes actual
				let diaDelMes = diaGlobal % diasPorMes;
				if (diaDelMes === 0) diaDelMes = diasPorMes;
				
				mesActual = Math.ceil(diaGlobal / diasPorMes);
				
				datos.push({
					dosis: redondear(dosis, 2),
					concentracion: concentracionMgPorMl,
					tasaInfusion: redondear(tasaInfusion, 3),
					volumenDiario: redondear(volumenDiario, 4),
					volumenInfusion: volumenInfusion,
					programacionBomba: programacionBomba,
					dia: diaDelMes,
					diaGlobal: diaGlobal,
					semana: semana + 1,
					mes: mesActual,
					cargaJeringa: null, // Se calculará después
					cargaPractica: null, // Se calculará después
					totalMensual: null, // Se calculará después
					esDia30: false // Por defecto no es día 30
				});
				
				diaGlobal++;
			}
		}
		
		// Calcular las cargas de jeringa
		const datosConCarga = calcularCargaJeringa(datos);
		
		// Calcular totales mensuales
		return calcularTotalMensual(datosConCarga);
	};

	const obtenerSemanasPorRango = () => {
		if (rango === "1-10") return { start: 0, end: 10 };
		if (rango === "11-20") return { start: 10, end: 20 };
		return { start: 20, end: 26 };
	};

	const { start, end } = obtenerSemanasPorRango();

	const calcularFilaSemana = (dosis) => { 
		const tasaInfusionMlH = calcularTasaInfusion(dosis);
		const volumenDiario = calcularVolumenDiario(tasaInfusionMlH);
		const { volumenInfusion, programacionBomba } = calcularVolumenYProgramacion(tasaInfusionMlH);
		
		return {
			dosis,
			tasaInfusion: redondear(tasaInfusionMlH, 3),
			volumenDiario: redondear(volumenDiario, 3),
			volumenInfusion: volumenInfusion,
			programacionBomba: programacionBomba
		};
	};

	const generarPDF = () => {
		const doc = new jsPDF();
		const datosPorDia = generarDatosPorDia();
		const usablePageWidth = doc.internal.pageSize.getWidth() - 10;

		// Título principal
		doc.setFontSize(16);
		doc.text(`Plan de titulación - Desglose diario`, 14, 15);
		doc.setFontSize(9);
		doc.text(
			"Este cálculo está con base en la recomendación de IPP Remodulin® autorizada en México de arrancar tratamiento con 1.25ng/kg/min",
			14,
			20
		);
		doc.setFontSize(12);
		doc.text(`Peso del paciente: ${peso} kg`, 14, 28);
		doc.text(`Concentración del vial: 5 mg/mL`, 14, 35);
		doc.text(`Meses considerados de ${diasPorMes} días`, 14, 42);

		// Preparar datos para la tabla
		const body = datosPorDia.map(dato => [
			dato.dia.toString(),
			dato.semana.toString(),
			dato.mes.toString(),
			dato.dosis.toString().replace('.', ','),
			`${dato.concentracion}`,
			formatPumpProgramming(dato.programacionBomba).replace('.', ','),
		]);

		// Crear tabla con desglose diario
		autoTable(doc, {
			head: [
				[
					"Día", 
					"Semana", 
					"Mes",
					"Dosis (ng/Kg/min)", 
					"Concentración vial (mg/mL)",
					"Programación Bomba de infusión (UI/h)",
					
				]
			],
			body,
			startY: 50,
			tableWidth: usablePageWidth,
			styles: { 
				halign: "center", 
				valign: "middle",
				fontSize: 8.5 // Aumentado de 4.5 a 8.5 (4 puntos más)
			},
			headStyles: { 
				fillColor: [214, 235, 176], 
				textColor: 20,
				fontSize: 8.5
			},
			bodyStyles: { 
				textColor: 50,
				fontSize: 8.5
			},
			// Aplicar estilo diferente para las filas del día 30
			didParseCell: function(data) {
				if (data.section === 'body' && data.row.index < datosPorDia.length) {
					const dato = datosPorDia[data.row.index];
					if (dato.esDia30) {
						data.cell.styles.fillColor = [230, 240, 255]; // Color azul claro
						data.cell.styles.textColor = 0; // Texto negro
						data.cell.styles.fontStyle = 'bold'; // Texto en negrita
					}
				}
			},
			columnStyles: { 
				0: { cellWidth: usablePageWidth * 0.08 },
				1: { cellWidth: usablePageWidth * 0.11 },
				2: { cellWidth: usablePageWidth * 0.08 },
				3: { cellWidth: usablePageWidth * 0.18 },
				4: { cellWidth: usablePageWidth * 0.2 },
				5: { cellWidth: usablePageWidth * 0.35 }
			},
			margin: { left: 5, right: 5 },
			// Agregar página automáticamente si la tabla es muy larga
			pageBreak: 'auto'
		});

		// Agregar nota explicativa
		const finalY = doc.lastAutoTable.finalY + 10;
		doc.setFontSize(10);
		doc.text("Nota: La dosis se mantiene constante durante toda la semana.", 14, finalY);
		doc.text("Cada cambio de dosis ocurre al inicio de una nueva semana.", 14, finalY + 5);
		doc.text("Los días se reinician en 1 al comenzar cada nuevo mes.", 14, finalY + 10);
		doc.text("Filas en azul: Día 30 de cada mes (resumen mensual)", 14, finalY + 15);

			doc.save("plan-titulacion-diario-completo.pdf");
		};

	return (
		<div className="container my-5 offset-lg-1 col-lg-10 offset-xl-2 col-xl-8 offset-xxl-3 col-xxl-6">
			<div className="text-center mb-3">
				<h5 className="txt-d-titulo">Tabla de dosificación</h5>
				<p className="txt-d-peso">Peso del paciente: {peso} kg</p>
			</div>

			<div className="position-realitve d-flex justify-content-between align-items-center mb-3">
				<select className="select-weeks" value={rango} onChange={(e) => setRango(e.target.value)}>
					<option value="1-10">Semanas 1-10</option>
					<option value="11-20">Semanas 11-20</option>
					<option value="21-26">Semanas 21-26</option>
				</select>
			</div>

			<div className="table-responsive">
				<table className="table text-center">
					<thead>
						<tr>
							<th className="table-title">Semana</th>
							<th className="table-title">
								Dosis<br />
								<small>ng/kg/min</small>
							</th>
							<th className="table-title-end">
								Programación<br />
								<small>Bomba de Infusión UI/h</small>
							</th>
						</tr>
					</thead>
					<tbody>
						{dosisPorSemana.slice(start, end).map((dosis, index) => {
							const semana = start + index + 1;
							const { programacionBomba } = calcularFilaSemana(dosis);
							return (
								<tr key={semana}>
									<td>{semana}</td>
									<td>{dosis}</td>
									<td className="table-column-end">{formatPumpProgramming(programacionBomba)}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<div className="text-center mt-4 pt-3 d-flex justify-content-center ">
				<div target="_blank" rel="noopener noreferrer" className="row text-decoration-none a-row-ficha" onClick={generarPDF}>
					<div className="col-3 h-100 px-0 d-flex align-items-center justify-content-center div-img-pdf">
						<img src="/images/icon-pdf.png" alt="" className="img-pdf" />
					</div>
					<div className="col-7 h-100 px-0 d-flex align-items-center justify-content-center div-txt-pdf">
						<p className="m-0 txt-pdf">Plan de titulación diario completo</p>
					</div>
					<div className="col-2 h-100 px-0 d-flex align-items-center justify-content-center  div-img-download">
						<img src="/images/icon-download.png" alt="" className="img-download" />
					</div>
				</div>
			</div>
		</div>
	);
};
